import { Box } from '@mui/material';
import {
    CloseFullscreenRounded,
    CloseRounded,
    EditRounded,
    KeyboardArrowDownRounded,
    KeyboardArrowRightRounded,
    KeyboardArrowUpRounded,
    OpenInFullRounded
} from "@mui-symbols-material/w200";
import { DescriptionOutlined } from "@mui-symbols-material/w300";
import React, {useEffect, useMemo, useState} from 'react';
import {Typography, IconButton, Tooltip, Button, CircularProgress} from '@mui/joy';
import {useUserResources} from "@dreebit-registry/vsm-microservice-components";
import formatEventEnum from "../../utils/formatEventEnum";
import {getFileDisplayName} from "../../utils/fileType";
import {useTranslation} from "react-i18next";
import {InstalledBaseEventById} from "../../graphql/queries/installedBaseEventById";
import {EventCategory, EventType} from "../../codegen/graphql";
import {EventNavigation, INSTALLED_BASE_EVENT_UPDATE_RESOURCE} from "../AssetEventList/table";
import EventDeclarationSection from "../ContaminationDeclarationStatus/EventDeclarationSection";
import {isContaminationDeclarationsEnabled} from "../../utils/contaminationDeclarationsEnabled";
import {EventDetailAttribute, splitEventAttributes} from "../../utils/eventDetailAttributes";
import {loadEventDetailPanelWide, saveEventDetailPanelWide} from "../../utils/eventDetailPanelStorage";
import {reportUnformattedEventAttributes} from "../../utils/reportUnformattedEventAttributes";
import {useQuery} from "@apollo/client";
import _ from "lodash";
import moment from 'moment';
import AssetNameplate from "../AssetNameplate";


interface Props {
    eventId: string | number
    onCloseEventDetailView: () => void
    onEditEvent?: (id: string | number) => void
    isGlobalEventList?: boolean
    navigation?: EventNavigation | null
}

// Arrow keys belong to whatever the user is typing in or picking from (filters,
// dropdowns, menus, the edit dialog) — only elsewhere do they page through events.
const ownsArrowKeys = (target: EventTarget | null): boolean => {
    const el = target as HTMLElement | null;
    if (!el?.closest) return false;
    return el.isContentEditable
        || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
        // The grid's columns panel reorders its rows with the arrow keys
        || !!el.closest('[role="listbox"], [role="menu"], [role="dialog"], [role="combobox"], .MuiDataGrid-panel');
};

const Chip = ({label, color, background}: {label: string, color: string, background: string}) => (
    <Box sx={{ bgcolor: background, px: '16px', py: '3px', borderRadius: '12px' }}>
        <Typography level="bodySmallBold" sx={{ color }}>
            {label}
        </Typography>
    </Box>
);

// Short values as stacked label/value cells; the column count follows the panel width
// (2 in the narrow panel, at most 4 in the wide one) unless `singleColumn` is set.
const FactGrid = ({facts, muted, singleColumn}: {facts: EventDetailAttribute[], muted?: boolean, singleColumn?: boolean}) => (
    <Box sx={{
        display: 'grid',
        gridTemplateColumns: singleColumn
            ? '1fr'
            : 'repeat(auto-fill, minmax(max(170px, calc((100% - 3 * 21px) / 4)), 1fr))',
        columnGap: '21px',
        rowGap: '13px',
    }}>
        {facts.map((fact) => (
            <Box key={fact.id} sx={{display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0}}>
                <Typography level="bodySmallBold" sx={{wordBreak: 'break-word', color: muted ? '#666666' : undefined}}>
                    {fact.label}
                </Typography>
                <Typography level="body" sx={{wordBreak: 'break-word', color: muted ? '#666666' : undefined}}>
                    {fact.value}
                </Typography>
            </Box>
        ))}
    </Box>
);

const AssetEventDetailView = ({eventId, onCloseEventDetailView, onEditEvent, isGlobalEventList, navigation}: Props) => {

    const {t} = useTranslation();
    const resources = useUserResources();
    const canEdit = resources.includes(INSTALLED_BASE_EVENT_UPDATE_RESOURCE);

    const [wide, setWide] = useState(loadEventDetailPanelWide);
    const [furtherDetailsOpen, setFurtherDetailsOpen] = useState(false);

    const toggleWide = () => {
        setWide((prev) => {
            saveEventDetailPanelWide(!prev);
            return !prev;
        });
    };

    // ↑ / ↓ page through the events. With the table focused its cell focus moves along,
    // so focus and the open event stay on the same row.
    useEffect(() => {
        if (!navigation) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || ownsArrowKeys(e.target)) return;
            if (e.key === 'ArrowUp' && navigation.hasPrev) {
                e.preventDefault();
                navigation.goPrev();
            } else if (e.key === 'ArrowDown' && navigation.hasNext) {
                e.preventDefault();
                navigation.goNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigation]);

    const { data, loading, error } = useQuery(InstalledBaseEventById, {
        variables: { eventId: eventId }
    });

    const eventData = _.get(data, 'installedBaseEvent');
    // Only lifecycle events carry editable TDAs; other categories never have any.
    const isLifecycleEvent = eventData?.eventCategory === EventCategory.LIFECYCLE;

    const parsedData = typeof eventData?.data === 'string'
        ? JSON.parse(eventData.data)
        : eventData?.data;

    const {longText, facts, secondary, changeSets, unformatted} = useMemo(
        () => splitEventAttributes(_.get(eventData, 'attributeList.attributes', []) ?? [], t),
        [eventData, t]
    );

    useEffect(() => {
        if (unformatted.length > 0) {
            reportUnformattedEventAttributes(unformatted, eventData?.eventType, eventData?.id);
        }
    }, [unformatted, eventData?.eventType, eventData?.id]);

    // Prefer the node path from the query; fall back to the raw data JSON.
    const pathNodes = _.get(eventData, 'installedBaseNode.pathNodes');
    const assetLocationPath = Array.isArray(pathNodes) && pathNodes.length > 0
        ? pathNodes.map((n: any) => n?.title).filter(Boolean).join(' / ')
        : (Array.isArray(parsedData?.installedBaseNodePath)
            ? [...parsedData.installedBaseNodePath].reverse().map((n: any) => _.startCase(n.key)).join(' / ')
            : '');

    const metaParts = [
        eventData?.insert ? moment(eventData.insert).format('L LT') : null,
        eventData?.user?.name,
    ].filter(Boolean);

    const chips = [
        parsedData?.planned === false && {label: t('unplanned'), color: '#333333', background: '#FFC860'},
        parsedData?.copperProcess === true && {label: t('Copper process'), color: 'white', background: '#666666'},
        parsedData?.strategicTool === true && {label: t('Strategic tool'), color: 'white', background: '#666666'},
        parsedData?.newPump === true && {label: t('New pump'), color: 'white', background: '#666666'},
    ].filter(Boolean) as {label: string, color: string, background: string}[];

    return (
        <Box
            data-testid="event-detail-panel"
            sx={{display:'flex', flexDirection:'column', flexShrink: 0, width: wide ? '50%' : 'clamp(400px, 32vw, 560px)',
                 height: '100%', minHeight: 0, gap: '13px', pl: '13px', pb:'13px', borderLeft: "1px solid #cccccc", ml: '12px', mb:'13px', mt:'12px'}}>
            <Box sx={{display: 'flex', flexDirection: 'column', flexShrink: 0, gap: '21px', px: '5px', pt: '21px', pb: '21px', borderBottom: "1px solid #cccccc"}}>

                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '13px'}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '13px', flexWrap: 'wrap'}}>
                            <Typography level="titleMedium">
                                {formatEventEnum(eventData?.eventType, t)}
                            </Typography>
                            {chips.map((chip) => <Chip key={chip.label} {...chip}/>)}
                        </Box>
                        {metaParts.length > 0 && (
                            <Typography level="caption" sx={{color: '#666666'}}>
                                {metaParts.join(' · ')}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0}}>
                        {navigation && (
                            <>
                                <Tooltip title={`${t('Previous event')} (↑)`} size="sm">
                                    <span>
                                        <IconButton onClick={navigation.goPrev}
                                                    disabled={!navigation.hasPrev}
                                                    variant="outlined"
                                                    color="neutral"
                                                    size="sm"
                                                    aria-label={t('Previous event')}
                                                    data-testid="event-detail-prev"
                                        >
                                            <KeyboardArrowUpRounded/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={`${t('Next event')} (↓)`} size="sm">
                                    <span>
                                        <IconButton onClick={navigation.goNext}
                                                    disabled={!navigation.hasNext}
                                                    variant="outlined"
                                                    color="neutral"
                                                    size="sm"
                                                    aria-label={t('Next event')}
                                                    data-testid="event-detail-next"
                                        >
                                            <KeyboardArrowDownRounded/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title={wide ? t('Collapse panel') : t('Expand panel')} size="sm">
                            <IconButton onClick={toggleWide}
                                        variant="outlined"
                                        color="neutral"
                                        size="sm"
                                        aria-label={wide ? t('Collapse panel') : t('Expand panel')}
                                        data-testid="event-detail-wide-toggle"
                            >
                                {wide ? <CloseFullscreenRounded/> : <OpenInFullRounded/>}
                            </IconButton>
                        </Tooltip>
                        {canEdit && onEditEvent && isLifecycleEvent && (
                            <IconButton onClick={() => onEditEvent(eventId)}
                                        variant="outlined"
                                        color="neutral"
                                        size="sm"
                            >
                                <EditRounded/>
                            </IconButton>
                        )}
                        <IconButton onClick={onCloseEventDetailView}
                                    variant="outlined"
                                    color="neutral"
                                    size="sm"
                        >
                            <CloseRounded/>
                        </IconButton>
                    </Box>
                </Box>

                {isGlobalEventList && eventData?.asset?.assetLocationList?.assetLocations?.[0]?.id && (
                <AssetNameplate assetLocationId={eventData.asset.assetLocationList.assetLocations[0].id} compact={true}/>
                )}

            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: '34px', overflowY: 'auto', px: '13px'}}>
                {/* The list can contain events whose detail the backend refuses (node permission). */}
                {!eventData && loading && <CircularProgress size="sm" sx={{alignSelf: 'center', mt: '21px'}}/>}
                {!eventData && !loading && error && (
                    <Typography level="body" sx={{color: '#666666'}} data-testid="event-detail-error">
                        {t('The event could not be loaded.')}
                    </Typography>
                )}

                {assetLocationPath && (
                    <Box sx={{display: 'flex', gap: '5px', flexDirection: 'column'}}>
                        <Typography level="bodySmallBold">{t('Asset Location')}</Typography>
                        <Typography level="body">
                            {assetLocationPath}
                        </Typography>
                    </Box>
                )}

                {eventData?.message && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <Typography level="bodyBold">
                            {t('Message')}
                        </Typography>
                        <Typography level="body" sx={{whiteSpace: 'pre-line', wordBreak: 'break-word'}}>
                            {eventData.message}
                        </Typography>
                    </Box>
                )}

                {longText.map((entry) => (
                    <Box key={entry.id} sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <Typography level="bodyBold">
                            {entry.label}
                        </Typography>
                        <Typography level="body" sx={{whiteSpace: 'pre-line', wordBreak: 'break-word'}}>
                            {entry.value}
                        </Typography>
                    </Box>
                ))}

                {facts.length > 0 && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '13px'}}>
                        <Typography level="bodyBold">
                            {t('Details')}
                        </Typography>
                        <FactGrid facts={facts}/>
                    </Box>
                )}

                {/* Before and after are full paths, so they stack instead of sharing a row */}
                {changeSets.map((changeSet) => (
                    <Box key={changeSet.id} sx={{display: 'flex', flexDirection: 'column', gap: '13px'}}>
                        <Typography level="bodyBold">
                            {changeSet.label}
                        </Typography>
                        <FactGrid facts={changeSet.facts} singleColumn/>
                    </Box>
                ))}

                {isContaminationDeclarationsEnabled() && isLifecycleEvent && eventData?.eventType === EventType.ASSET_REMOVE && (
                    <EventDeclarationSection eventId={String(eventId)}/>
                )}

                {(eventData?.fileList?.installedBaseNodeFiles?.length ?? 0) > 0 && (
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                        <Typography level="bodyBold">
                            {t('Files')}
                        </Typography>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px', pl: '8px', pt: '13px'}}>
                            {eventData.fileList.installedBaseNodeFiles.map((file: any) => {
                                const contentType = file?.file?.contentType || '';
                                const isImage = contentType.startsWith('image/') && file?.file?.thumbnailLink;
                                const link = file?.file?.link;
                                return (
                                    <Box
                                        key={file.id}
                                        component="a"
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            border: '1px solid #eee',
                                            borderRadius: '5px',
                                            px: '8px',
                                            py: '6px',
                                            '&:hover': {backgroundColor: '#f9f9f9'},
                                        }}
                                    >
                                        {isImage ? (
                                            <img
                                                src={file.file.thumbnailLink}
                                                alt={file.filename}
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    objectFit: 'cover',
                                                    borderRadius: 4,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f4f4f4',
                                                    borderRadius: '4px',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <DescriptionOutlined sx={{color: '#666'}}/>
                                            </Box>
                                        )}
                                        <Typography
                                            level="titleSmallLight"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            {getFileDisplayName({basename: file?.file?.basename, filename: file.filename})}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {secondary.length > 0 && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '13px'}}>
                        <Button
                            variant="plain"
                            color="neutral"
                            size="sm"
                            onClick={() => setFurtherDetailsOpen((open) => !open)}
                            startDecorator={
                                <KeyboardArrowRightRounded sx={{
                                    transition: 'transform 0.2s',
                                    transform: furtherDetailsOpen ? 'rotate(90deg)' : 'none',
                                }}/>
                            }
                            aria-expanded={furtherDetailsOpen}
                            data-testid="event-detail-further"
                            sx={{alignSelf: 'flex-start', px: '4px', ml: '-4px'}}
                        >
                            {`${t('Further details')} (${secondary.length})`}
                        </Button>
                        {furtherDetailsOpen && <FactGrid facts={secondary} muted/>}
                    </Box>
                )}
            </Box>

        </Box>
    );
}

export default AssetEventDetailView;
